import { PurchasesEntitlementInfo, PurchasesPackage } from '@revenuecat/purchases-capacitor';

/**
 * Getters for the Subscription store module
 *
 * @author Matt Kenefick <matt@polymermallard.com>
 * @package Store/Module/Subscription
 * @project ChalkySticks
 */
const getters = {
	/**
	 * Get active entitlements
	 *
	 * @param IRevenueCatState state
	 * @return Record<string, PurchasesEntitlementInfo>
	 */
	activeEntitlements(state: IRevenueCatState): Record<string, PurchasesEntitlementInfo> {
		return state.customerInfo?.entitlements?.active || {};
	},

	/**
	 * Get all entitlements (active and inactive)
	 *
	 * @param IRevenueCatState state
	 * @return Record<string, PurchasesEntitlementInfo>
	 */
	allEntitlements(state: IRevenueCatState): Record<string, PurchasesEntitlementInfo> {
		return state.customerInfo?.entitlements?.all || {};
	},

	/**
	 * Get available packages from current offering
	 *
	 * @param IRevenueCatState state
	 * @return PurchasesPackage[]
	 */
	availablePackages(state: IRevenueCatState): PurchasesPackage[] {
		return state.availableOfferings?.current?.availablePackages || [];
	},

	/**
	 * Check if user can make payments
	 *
	 * @param IRevenueCatState state
	 * @return boolean
	 */
	canMakePayments(state: IRevenueCatState): boolean {
		// If we have offerings, we can make payments
		return state.availableOfferings !== null;
	},

	/**
	 * Get current offering
	 *
	 * @param IRevenueCatState state
	 * @return any
	 */
	currentOffering(state: IRevenueCatState): any {
		return state.availableOfferings?.current || null;
	},

	/**
	 * Get expiration date of active subscription
	 *
	 * @param IRevenueCatState state
	 * @return string | null
	 */
	expirationDate(state: IRevenueCatState): string | null {
		const activeEntitlements = state.customerInfo?.entitlements?.active || {};
		const firstEntitlement = Object.values(activeEntitlements)[0];
		return firstEntitlement?.expirationDate || null;
	},

	/**
	 * Check if user has specific entitlement
	 *
	 * @param IRevenueCatState state
	 * @return (entitlementId: string) => boolean
	 */
	hasEntitlement(state: IRevenueCatState): (entitlementId: string) => boolean {
		return (entitlementId: string): boolean => {
			const activeEntitlements = state.customerInfo?.entitlements?.active || {};
			return activeEntitlements[entitlementId] !== undefined;
		};
	},

	/**
	 * Check if user has premium access
	 *
	 * @param IRevenueCatState state
	 * @return boolean
	 */
	hasPremium(state: IRevenueCatState): boolean {
		return state.hasPremium;
	},

	/**
	 * Check if subscription module is initialized
	 *
	 * @param IRevenueCatState state
	 * @return boolean
	 */
	isInitialized(state: IRevenueCatState): boolean {
		return state.customerInfo !== null && state.availableOfferings !== null;
	},

	/**
	 * Check if offerings are loading
	 *
	 * @param IRevenueCatState state
	 * @return boolean
	 */
	isLoadingOfferings(state: IRevenueCatState): boolean {
		return state.isLoadingOfferings;
	},

	/**
	 * Check if purchase is in progress
	 *
	 * @param IRevenueCatState state
	 * @return boolean
	 */
	isPurchasing(state: IRevenueCatState): boolean {
		return state.isPurchasing;
	},

	/**
	 * Check if restore is in progress
	 *
	 * @param IRevenueCatState state
	 * @return boolean
	 */
	isRestoring(state: IRevenueCatState): boolean {
		return state.isRestoring;
	},

	/**
	 * Check if subscription is active
	 *
	 * @param IRevenueCatState state
	 * @return boolean
	 */
	isSubscribed(state: IRevenueCatState): boolean {
		const activeEntitlements = state.customerInfo?.entitlements?.active || {};
		return Object.keys(activeEntitlements).length > 0;
	},

	/**
	 * Check if subscription will renew
	 *
	 * @param IRevenueCatState state
	 * @return boolean
	 */
	isSubscriptionActive(state: IRevenueCatState): boolean {
		const activeEntitlements = state.customerInfo?.entitlements?.active || {};
		const firstEntitlement = Object.values(activeEntitlements)[0];
		return firstEntitlement?.willRenew || false;
	},

	/**
	 * Get last error message
	 *
	 * @param IRevenueCatState state
	 * @return string | null
	 */
	lastError(state: IRevenueCatState): string | null {
		return state.lastError;
	},

	/**
	 * Get monthly package if available
	 *
	 * @param IRevenueCatState state
	 * @return PurchasesPackage | null
	 */
	monthlyPackage(state: IRevenueCatState): PurchasesPackage | null {
		const packages = state.availableOfferings?.current?.availablePackages || [];
		return packages.find((pkg) => pkg.packageType === 'MONTHLY') || null;
	},

	/**
	 * Get time since last status check in milliseconds
	 *
	 * @param IRevenueCatState state
	 * @return number | null
	 */
	timeSinceLastCheck(state: IRevenueCatState): number | null {
		if (!state.lastStatusCheck) {
			return null;
		}
		return Date.now() - state.lastStatusCheck;
	},

	/**
	 * Get user ID from customer info
	 *
	 * @param IRevenueCatState state
	 * @return string | null
	 */
	userId(state: IRevenueCatState): string | null {
		return state.customerInfo?.originalAppUserId || null;
	},

	/**
	 * Get yearly package if available
	 *
	 * @param IRevenueCatState state
	 * @return PurchasesPackage | null
	 */
	yearlyPackage(state: IRevenueCatState): PurchasesPackage | null {
		const packages = state.availableOfferings?.current?.availablePackages || [];
		return packages.find((pkg) => pkg.packageType === 'ANNUAL') || null;
	},
};

export default getters;
